import { Component, DestroyRef, inject, Inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faCheck, faListCheck, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import { ErrorService } from '../../../../../../@core/utils/error.service';
import { ConfirmationDialogComponent } from '../../../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component';
import { TenantWordsPipe } from '../../../../../core/pipes/tenant-words.pipe';
import { TransactionService } from '../../../../../core/services/TransactionService';
import { ReasonCode } from '../../../models/reason-code';

export interface ReasonCodeEditData {
  reasonCode?: ReasonCode;
}

/** Creates, renames or deletes a reason code. Closes with true once saved or deleted. */
@Component({
  selector: 'reason-code-edit',
  templateUrl: './reason-code-edit.component.html',
  styleUrls: ['./reason-code-edit.component.scss']
})
export class ReasonCodeEditComponent {

  static open(dialog: MatDialog, data: ReasonCodeEditData): MatDialogRef<ReasonCodeEditComponent, boolean> {
    return dialog.open(ReasonCodeEditComponent, { data, width: '480px', maxWidth: '95vw' });
  }

  public faCheck = faCheck;
  public faListCheck = faListCheck;
  public faTrash = faTrash;
  public faXmark = faXmark;

  /** A copy, so cancelling leaves the list's row untouched. */
  public reasonCode: ReasonCode;
  public isSubmitting = false;

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ReasonCodeEditData,
    private dialogRef: MatDialogRef<ReasonCodeEditComponent, boolean>,
    private transactionService: TransactionService,
    private errorService: ErrorService,
    private dialog: MatDialog,
    private tenantWords: TenantWordsPipe
  ) {
    this.reasonCode = { ...data?.reasonCode };
  }

  onSubmit() {
    if (!this.reasonCode.reason?.trim()) return;

    this.isSubmitting = true;
    const request = this.reasonCode.id
      ? this.transactionService.updateCode(this.reasonCode)
      : this.transactionService.createCode(this.reasonCode);

    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.dialogRef.close(true),
      error: err => {
        this.isSubmitting = false;
        this.errorService.handleError(err);
      }
    });
  }

  onDelete() {
    this.dialog.open(ConfirmationDialogComponent, {
      minWidth: '10%',
      data: {
        confirmLabel: 'Yes',
        title: `Delete ${this.tenantWords.transform('Reason Code')}?`,
        confirmMessage: `Are you sure you want to delete ${this.reasonCode.reason}?`,
        cancelLabel: 'No'
      }
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (confirmed !== true) return;

      this.isSubmitting = true;
      this.transactionService.deleteCode(this.reasonCode.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => this.dialogRef.close(true),
        error: err => {
          this.isSubmitting = false;
          this.errorService.handleError(err);
        }
      });
    });
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
